import { EmblaCarousel } from '../vendor/embla.js';

class LkHeroSlider extends HTMLElement {
  connectedCallback() {
    const viewport = this.querySelector('[data-hero-viewport]');
    if (!viewport || this.embla) return;
    this.allSlides = [...this.querySelectorAll('[data-hero-slide]')];
    if (!this.allSlides.length) return;
    this.mobile = window.matchMedia('(max-width: 989px)');
    this.slides = this.allSlides;
    this.status = this.querySelector('[data-hero-status]');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.hovered = false;
    this.focused = false;
    this.dragging = false;
    this.delay = Number(this.dataset.autoplayDelay) * 1000;
    this.embla = EmblaCarousel(viewport, { loop: true, duration: 20, slides: this.slides });
    this.update = () => {
      this.slides = this.embla.slideNodes();
      const selected = this.embla.selectedScrollSnap();
      this.allSlides.forEach((slide) => {
        const active = slide === this.slides[selected];
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      if (this.status) this.status.textContent = `${this.slides[selected].dataset.heroLabel} (${selected + 1}/${this.slides.length})`;
      this.schedule();
    };
    this.onLayout = () => {
      const startIndex = this.embla.selectedScrollSnap();
      const hadFocus = this.contains(document.activeElement);
      this.embla.reInit({ slides: this.slides, startIndex });
      if (hadFocus) {
        const link = this.mobile.matches ? '.lk-pdf-hero__scene' : '.lk-pdf-hero__desktop';
        this.slides[startIndex].querySelector(link)?.focus({ preventScroll: true });
      }
    };
    this.schedule = () => {
      clearTimeout(this.timer);
      if (!this.delay || this.hovered || this.focused || this.dragging || this.motion.matches || document.hidden || window.Shopify?.designMode) return;
      this.timer = setTimeout(() => this.embla.scrollNext(), this.delay);
    };
    this.onVisibility = () => this.schedule();
    this.onEnter = () => { this.hovered = this.hoverMedia.matches; this.schedule(); };
    this.onLeave = () => { this.hovered = false; this.schedule(); };
    this.onHoverChange = () => { this.hovered = this.hoverMedia.matches && this.matches(':hover'); this.schedule(); };
    this.onFocus = () => { this.focused = true; this.status?.setAttribute('aria-live', 'polite'); this.schedule(); };
    this.onBlur = (event) => {
      if (this.contains(event.relatedTarget)) return;
      this.focused = false;
      this.status?.setAttribute('aria-live', this.delay ? 'off' : 'polite');
      this.schedule();
    };
    this.onBlockSelect = (event) => {
      const slide = event.target.closest('[data-hero-slide]');
      const index = this.slides.indexOf(slide);
      if (index >= 0) this.embla.scrollTo(index, true);
    };
    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
    this.addEventListener('focusin', this.onFocus);
    this.addEventListener('focusout', this.onBlur);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.motion.addEventListener('change', this.onVisibility);
    this.hoverMedia.addEventListener('change', this.onHoverChange);
    this.mobile.addEventListener('change', this.onLayout);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.embla.on('select', this.update);
    this.embla.on('reInit', this.update);
    this.embla.on('pointerDown', () => { this.dragging = true; this.schedule(); });
    this.embla.on('pointerUp', () => { this.dragging = false; this.schedule(); });
    this.update();
    if (this.delay) this.status?.setAttribute('aria-live', 'off');
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.removeEventListener('mouseenter', this.onEnter);
    this.removeEventListener('mouseleave', this.onLeave);
    this.removeEventListener('focusin', this.onFocus);
    this.removeEventListener('focusout', this.onBlur);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motion?.removeEventListener('change', this.onVisibility);
    this.hoverMedia?.removeEventListener('change', this.onHoverChange);
    this.mobile?.removeEventListener('change', this.onLayout);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.embla?.destroy();
    this.embla = null;
    this.allSlides?.forEach((slide) => {
      slide.inert = false;
      slide.removeAttribute('aria-hidden');
    });
  }
}

if (!customElements.get('lk-hero-slider')) customElements.define('lk-hero-slider', LkHeroSlider);
